import { baseApi } from './baseApi';
import type {
  ExtraService,
  Location,
  LocationRole,
  Quote,
  QuoteRequest,
  Trip,
  TripDetail,
} from './types';

/**
 * Catalog and quoting endpoints.
 *
 * Search is symmetric: `useConnectionsQuery` serves both "where can I go from
 * here" and "where can I come from" depending on the role passed, which is what
 * lets the customer start from either end.
 */
export const catalogApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    locations: builder.query<Location[], LocationRole>({
      query: (role) => ({ url: 'public/locations', params: { role } }),
      providesTags: ['Location'],
    }),

    connections: builder.query<Location[], { slug: string; role: LocationRole }>({
      query: ({ slug, role }) => ({
        url: `public/locations/${encodeURIComponent(slug)}/connections`,
        params: { role },
      }),
      providesTags: ['Location'],
    }),

    tripByRoute: builder.query<TripDetail, { origin: string; destination: string }>({
      query: ({ origin, destination }) => ({
        url: 'public/trips',
        params: { origin, destination },
      }),
      providesTags: ['Trip'],
    }),

    trip: builder.query<TripDetail, string>({
      query: (slug) => `public/trips/${encodeURIComponent(slug)}`,
      providesTags: ['Trip'],
    }),

    extraServices: builder.query<ExtraService[], void>({
      query: () => 'public/extra-services',
      providesTags: ['ExtraService'],
    }),

    // A shortcut list for the landing page: the busiest trips, busiest first.
    frequentTrips: builder.query<Trip[], void>({
      query: () => 'public/trips/frequent',
      providesTags: ['Trip'],
    }),

    featuredTrips: builder.query<Trip[], void>({
      query: () => 'public/trips/featured',
      providesTags: ['Trip'],
    }),

    // A mutation rather than a query because it is a POST, but it creates
    // nothing: the price card re-runs it on every change to the configuration.
    quote: builder.mutation<Quote, QuoteRequest>({
      query: (body) => ({ url: 'public/quotes', method: 'POST', body }),
    }),
  }),
});

export const {
  useLocationsQuery,
  useConnectionsQuery,
  useTripByRouteQuery,
  // Lazy variant: the search resolves a pair of locations to the trip's real
  // slug on submit, rather than guessing the URL from the two slugs.
  useLazyTripByRouteQuery,
  useTripQuery,
  useExtraServicesQuery,
  useFrequentTripsQuery,
  useFeaturedTripsQuery,
  useQuoteMutation,
} = catalogApi;
